import request from '@/assets/common/request/request'

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80'

function pick(source, keys, fallback = '') {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null && source[key] !== '') {
      return source[key]
    }
  }
  return fallback
}

function toArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
      try {
        const parsed = JSON.parse(trimmed)
        return toArray(parsed)
      } catch (error) {
        // Fall back to comma splitting below.
      }
    }
    return value.split(',').map(item => item.trim()).filter(Boolean)
  }
  return []
}

function toObject(value) {
  if (!value) return {}
  if (typeof value === 'object') return value
  if (typeof value === 'string') {
    const source = value.trim()
    try {
      const parsed = JSON.parse(source)
      return typeof parsed === 'string' ? toObject(parsed) : parsed
    } catch (error) {
      try {
        return JSON.parse(source.replace(/\\"/g, '"'))
      } catch (secondError) {
        return {}
      }
    }
  }
  return {}
}

function normalizeAttributes(...sources) {
  const merged = {}

  sources.forEach((source) => {
    Object.entries(source || {}).forEach(([name, value]) => {
      const values = toArray(value)
      if (name && values.length) {
        merged[name] = values
      }
    })
  })

  return Object.entries(merged).map(([name, values]) => ({ name, values }))
}

function toNumber(value, fallback = 0) {
  const num = Number(value)
  return Number.isNaN(num) ? fallback : num
}

function normalizeImage(value) {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    return pick(value, ['url', 'src', 'image', 'picUrl', 'imgUrl'], '')
  }
  return ''
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  const data = payload || {}
  const nested = data.content || data.data || data.result || data.page || data.pageInfo || data.goods || {}
  const candidates = [
    data.records,
    data.list,
    data.rows,
    data.items,
    nested.records,
    nested.list,
    nested.rows,
    nested.items,
    nested.content,
    Array.isArray(nested) ? nested : null
  ]

  return candidates.find(Array.isArray) || []
}

function extractTotal(payload, listLength) {
  const data = payload || {}
  const nested = data.content || data.data || data.result || data.page || data.pageInfo || {}
  return toNumber(pick(data, ['total', 'totalCount', 'count'], pick(nested, ['total', 'totalCount', 'count'], listLength)), listLength)
}

export function normalizeGoods(raw) {
  const code = String(pick(raw, ['code', 'goodsCode', 'productCode', 'skuCode', 'id', 'goodsId']))
  const mainImage = normalizeImage(pick(raw, ['image', 'mainImage', 'mainImg', 'picUrl', 'imgUrl', 'cover', 'goodsImg', 'thumbnail'], ''))
  const gallery = toArray(pick(raw, ['imageUrls', 'gallery', 'images', 'imageList', 'imgs', 'albumPics'], []))
    .map(normalizeImage)
    .filter(Boolean)
  const attribute = toObject(raw.attribute)
  const specifications = toObject(raw.specifications)
  const attributes = normalizeAttributes(attribute, specifications)
  const sizes = toArray(
    pick(raw, ['sizes', 'sizeList', 'size', 'specs'], '') ||
    attribute.Size ||
    attribute['Size(US)'] ||
    attribute.size ||
    specifications.Size ||
    specifications['Size(US)']
  )
  const colors = toArray(attribute.Color || attribute.color || specifications.Color || specifications.color)
  const price = toNumber(pick(raw, ['price', 'salePrice', 'sellingPrice', 'currentPrice', 'minPrice'], 0))
  const marketPrice = toNumber(pick(raw, ['oldPrice', 'marketPrice', 'originalPrice', 'linePrice'], 0))

  if (mainImage && !gallery.includes(mainImage)) {
    gallery.unshift(mainImage)
  }

  return {
    id: code,
    code,
    raw,
    name: pick(raw, ['name', 'goodsName', 'productName', 'title'], 'Untitled Product'),
    category: pick(raw, ['categoryName', 'categoryFullName', 'category', 'categoryType'], 'Menswear'),
    categoryType: pick(raw, ['categoryType', 'categoryName', 'categoryCode', 'type'], ''),
    price,
    oldPrice: marketPrice > price ? marketPrice : 0,
    currency: pick(raw, ['currency'], 'USD'),
    badge: pick(raw, ['badge', 'tag', 'label'], ''),
    color: pick(raw, ['color', 'colour', 'colorName'], colors[0] || 'As shown'),
    sizes: sizes.length ? sizes : ['S', 'M', 'L', 'XL'],
    attributes,
    image: gallery[0] || mainImage || PLACEHOLDER_IMAGE,
    gallery: gallery.length ? gallery : [PLACEHOLDER_IMAGE],
    description: pick(raw, ['description', 'desc', 'detail', 'remark', 'subTitle', 'subtitle'], 'A clean menswear staple selected for everyday styling.')
  }
}

export async function queryGoods(params = {}) {
  const payload = {
    num: params.num || 1,
    size: params.size || 12
  }

  if (params.categoryType) payload.categoryType = params.categoryType
  if (params.code) payload.code = params.code

  const res = await request.queryShopProduct(payload)
  const list = extractList(res).map(normalizeGoods)

  return {
    list,
    total: extractTotal(res, list.length),
    raw: res
  }
}

export async function queryGoodsDetail(code) {
  const res = await queryGoods({ code, num: 1, size: 1 })
  return res.list[0] || null
}
